import { getPlayerData } from "core-integration/src/store/player/reducers";
import React, { Suspense, lazy, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  Navigate,
  Outlet,
  Route,
  Routes as RouterRoutes,
  useLocation,
} from "react-router";
import { Loading } from "./App";
import Copy from "./Copy";
import EntryHistory from "./EntryHistory";
import EntryUnsubscribe from "./EntryUnsubscribe";
import EntryUpdate from "./EntryUpdate";
import FixturesWrapper from "./FixturesWrapper";
import Footer from "./Footer";
import Home from "./Home";
import { Main, Wrapper } from "./Layout";
import Login from "./Login";
import TransfersHistory from "./TransfersHistory";
import PlayerList from "./help/PlayerList";
import AdminClassic from "./leagues/AdminClassic";
import AdminH2H from "./leagues/AdminH2H";
import AutoJoin from "./leagues/AutoJoin";
import Create from "./leagues/Create";
import CreateClassic from "./leagues/CreateClassic";
import CreateH2H from "./leagues/CreateH2H";
import CreateJoin from "./leagues/CreateJoin";
import CupNotStarted from "./leagues/CupNotStarted";
import Invite from "./leagues/Invite";
import Join from "./leagues/Join";
import JoinPrivate from "./leagues/JoinPrivate";
import JoinPublic from "./leagues/JoinPublic";
import MatchesCup from "./leagues/MatchesCup";
import MatchesH2H from "./leagues/MatchesH2H";
import MyCups from "./leagues/MyCups";
import MyLeagues from "./leagues/MyLeagues";
import NewEntriesH2H from "./leagues/NewEntriesH2H";
import Renew from "./leagues/Renew";
import StandingsClassic from "./leagues/StandingsClassic";
import StandingsH2H from "./leagues/StandingsH2H";
import LoggedIn from "./player/LoggedIn";
import Logout from "./player/Logout";
import PasswordReset from "./player/PasswordReset";
import PasswordSet from "./player/PasswordSet";
import Register from "./player/Register";
import Scout from "./scout/Scout";
import SquadSelection from "./squad/SquadSelection";
import Transfers from "./squad/Transfers";
import News from "./stats/News";
import Statistics from "./stats/Statistics";
import DreamTeam from "./team/DreamTeam";
import EntryEvent from "./team/EntryEvent";
import MyTeam from "./team/MyTeam";
import XStats from "./xStats/XStats";

const Help = lazy(() => import("./help/Help"));
const Rules = lazy(() => import("./help/Rules"));
const Terms = lazy(() => import("./help/Terms"));
const Prizes = lazy(() => import("./Prizes"));

const OnRouteChange: React.FC<{ action?: () => void }> = ({ action }) => {
  const location = useLocation();
  useEffect(() => {
    if (action) action();
  }, [location.pathname]);

  return null;
};

const PlayerRoute = () => {
  const player = useSelector(getPlayerData);
  if (player && !player.entry) {
    return <Outlet />;
  } else if (player && player.dirty) {
    return <Register />;
  } else {
    return (
      <Wrapper>
        <Main isWide>
          <Login />
        </Main>
      </Wrapper>
    );
  }
};

const EntryRoute = () => {
  const player = useSelector(getPlayerData);
  if (player && player.entry && !player.dirty) {
    return <Outlet />;
  } else if (player && player.dirty) {
    return <Register />;
  } else {
    return (
      <Wrapper>
        <Main isWide>
          <Login />
        </Main>
      </Wrapper>
    );
  }
};

export const NotFound = () => {
  const { t } = useTranslation();
  return (
    <Wrapper>
      <Main>
        <Copy>
          <h4>{t("routes.notFound.title", "Page not found")}</h4>
          <p>
            {t(
              "routes.notFound.text",
              "Sorry, but the page you were looking for can't be found.",
            )}
          </p>
        </Copy>
      </Main>
    </Wrapper>
  );
};

const Routes: React.FC = () => {
  return (
    <Suspense fallback={<Loading />}>
      <RouterRoutes>
        <Route path="/" element={<Home />} />
        <Route element={<PlayerRoute />}>
          <Route element={<SquadSelection />} path="/squad-selection" />
        </Route>
        <Route element={<EntryRoute />}>
          <Route path="/my-team" element={<MyTeam />} />
          <Route path="/transfers" element={<Transfers />} />
          <Route path="/leagues" element={<MyLeagues />} />
          <Route path="/leagues/renew" element={<Renew />} />
          <Route path="/leagues/cups" element={<MyCups />} />
          <Route path="/leagues/create-join" element={<CreateJoin />} />
          <Route path="/leagues/create" element={<Create />} />
          <Route path="/leagues/create/classic" element={<CreateClassic />} />
          <Route path="/leagues/create/h2h" element={<CreateH2H />} />
          <Route path="/leagues/join" element={<Join />} />
          <Route path="/leagues/join/private" element={<JoinPrivate />} />
          <Route path="/leagues/join/public" element={<JoinPublic />} />
          <Route path="/leagues/:leagueId/admin/c" element={<AdminClassic />} />
          <Route path="/leagues/:leagueId/admin/h" element={<AdminH2H />} />
          <Route path="/leagues/:leagueId/invite" element={<Invite />} />
          <Route path="/entry-update" element={<EntryUpdate />} />
          <Route path="/leagues/:leagueId/cup" element={<MatchesCup />} />
          <Route
            path="/leagues/:leagueId/standings/c"
            element={<StandingsClassic />}
          />
        </Route>

        <Route
          path="/leagues/:leagueId/cup-not-started"
          element={<CupNotStarted />}
        />
        <Route
          path="/leagues/:leagueId/standings/h"
          element={<StandingsH2H />}
        />
        <Route path="/leagues/:leagueId/matches/h" element={<MatchesH2H />} />
        <Route
          path="/leagues/:leagueId/new-entries/h"
          element={<NewEntriesH2H />}
        />

        <Route path="/entry/:entryId/history" element={<EntryHistory />} />
        <Route path="/entry/:entryId/event/:eventId" element={<EntryEvent />} />
        <Route
          path="/entry/:entryId/transfers"
          element={<TransfersHistory />}
        />
        <Route path="/dream-team/" element={<DreamTeam />} />
        <Route path="/dream-team/:eventId" element={<DreamTeam />} />
        <Route path="/the-scout" element={<Scout />} />
        <Route path="/statistics" element={<Statistics />} />
        <Route path="/xstats" element={<XStats />} />
        <Route path="/statistics/:statName" element={<Statistics />} />
        <Route path="/the-scout/player-news" element={<News />} />
        <Route path="/fixtures" element={<FixturesWrapper />} />
        <Route path="/fixtures/:eventId" element={<FixturesWrapper />} />
        <Route path="/help" element={<Help />} />
        <Route path="/help/rules" element={<Rules />} />
        <Route path="/help/terms" element={<Terms />} />
        <Route path="/player-list" element={<PlayerList />} />
        <Route path="/prizes" element={<Prizes />} />
        <Route path="/leagues/auto-join/:code" element={<AutoJoin />} />
        <Route path="/player/logout" element={<Logout />} />
        <Route path="/player" element={<Register />} />
        <Route path="/player/password-reset/" element={<PasswordReset />} />
        <Route path="/player/password-set/:uidb64/" element={<PasswordSet />} />
        <Route
          path="/player/password-set/:uidb64/:token/"
          element={<PasswordSet />}
        />
        <Route path="/player/logged-in" element={<LoggedIn />} />
        <Route path="/a/team/my" element={<Navigate to="/my-team" replace />} />
        <Route
          path="/a/squad/transfers"
          element={<Navigate to="/transfers" replace />}
        />
        <Route
          path="/a/leagues/main"
          element={<Navigate to="/leagues" replace />}
        />

        <Route
          path="/a/fixtures"
          element={<Navigate to="/fixtures" replace />}
        />
        <Route
          path="/a/statistics/total_points"
          element={<Navigate to="/statistics" replace />}
        />
        <Route
          path="/entry/unsubscribe/:signature"
          element={<EntryUnsubscribe />}
        />
        <Route path="*" element={<NotFound />} />
      </RouterRoutes>
      <OnRouteChange action={() => window.scrollTo(0, 0)} />
      <Footer />
    </Suspense>
  );
};

export default Routes;
